import React, { useState } from "react";
import { Button, Box, CircularProgress } from "@mui/material";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";

const DownloadButtons = ({ transactions, fetchAllTransactions }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadExcel = async () => {
    setIsDownloading(true);
    try {
      // Fetch all transactions
      const allTransactions = await fetchAllTransactions();
      
      if (!allTransactions || allTransactions.length === 0) {
        toast.error("No data available to download");
        return;
      }

      // Prepare data for Excel with proper formatting
      const excelData = allTransactions.map((txn) => ({
        'Transaction ID': txn.tId,
        'Receiver ID': txn.receiverId,
        'Reverse By': txn.reverseBy,
        'Receiver Name': txn.receiverName,
        'User Type': txn.utype,
        'Amount': Number(txn.amount),
        'Date': new Date(txn.dt).toLocaleString(),
        'Remark': txn.remark || 'N/A',
        'Opening Balance': `₹${Number(txn.userWltOpeningBlc).toFixed(2)}`,
        'Closing Balance': `₹${Number(txn.userWltClosingBlc).toFixed(2)}`,
        'Status': txn.status
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      const wscols = [
        {wch: 15},  // Transaction ID
        {wch: 15},  // Receiver ID
        {wch: 15},  // Reverse By
        {wch: 20},  // Receiver Name
        {wch: 12},  // User Type
        {wch: 15},  // Amount
        {wch: 25},  // Date
        {wch: 30},  // Remark
        {wch: 15},  // Opening Balance
        {wch: 15},  // Closing Balance
        {wch: 12}   // Status
      ];
      ws['!cols'] = wscols;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Reverse Money Transactions");

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reverse-money-transactions-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Excel file downloaded successfully");
    } catch (error) {
      console.error("Error generating Excel:", error);
      toast.error("Failed to generate Excel file");
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadPDF = async () => {
    setIsDownloading(true);
    try {
      // Fetch all transactions
      const allTransactions = await fetchAllTransactions();
      
      if (!allTransactions || allTransactions.length === 0) {
        toast.error("No data available to download");
        return;
      }

      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Add header
      doc.setFontSize(20);
      doc.setTextColor(2, 123, 254); // Blue color
      doc.text('Ipaisa Reverse Money Transactions Report', 14, 20);
      
      // Add generation timestamp
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);
      
      // Add summary information
      doc.setFontSize(12);
      doc.text(`Total Transactions: ${allTransactions.length}`, 14, 40);
      
      // Calculate total amount
      const totalAmount = allTransactions.reduce((sum, txn) => sum + Number(txn.amount), 0);
      doc.text(`Total Amount: ₹${totalAmount.toFixed(2)}`, 14, 50);

      doc.autoTable({
        head: [['Transaction ID', 'Receiver ID', 'Reverse By', 'Receiver Name', 'User Type', 'Amount', 'Date', 'Remark', 'Opening Balance', 'Closing Balance', 'Status']],
        body: allTransactions.map(txn => [
          txn.tId,
          txn.receiverId,
          txn.reverseBy,
          txn.receiverName,
          txn.utype,
          `₹${Number(txn.amount).toFixed(2)}`,
          new Date(txn.dt).toLocaleString(),
          txn.remark || 'N/A',
          `₹${Number(txn.userWltOpeningBlc).toFixed(2)}`,
          `₹${Number(txn.userWltClosingBlc).toFixed(2)}`,
          txn.status
        ]),
        startY: 60,
        theme: 'grid',
        styles: { 
          fontSize: 8, 
          cellPadding: 2,
          overflow: 'linebreak'
        },
        headerStyles: { 
          fillColor: [2, 123, 254],
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        columnStyles: {
          0: { cellWidth: 25 },  // Transaction ID
          1: { cellWidth: 20 },  // Receiver ID
          2: { cellWidth: 20 },  // Reverse By
          3: { cellWidth: 25 },  // Receiver Name
          4: { cellWidth: 15 },  // User Type
          5: { cellWidth: 15 },  // Amount
          6: { cellWidth: 30 },  // Date
          7: { cellWidth: 30 },  // Remark
          8: { cellWidth: 20 },  // Opening Balance
          9: { cellWidth: 20 },  // Closing Balance
          10: { cellWidth: 15 }  // Status
        },
        margin: { top: 60 }
      });
      
      doc.save(`reverse-money-transactions-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success("PDF file downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
        marginBottom: "1rem",
        flexWrap: "wrap",
      }}
    >
      <Button
        variant="contained"
        sx={{
          backgroundColor: "#027BFE",
          color: "white",
          textTransform: "none",
          borderRadius: "8px",
          padding: "8px 16px",
          "&:hover": { backgroundColor: "#0056b3" },
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: "120px"
        }}
        onClick={downloadExcel}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <>
            <CircularProgress size={20} color="inherit" />
            Downloading...
          </>
        ) : (
          "Download Excel"
        )}
      </Button>
      <Button
        variant="contained"
        sx={{
          backgroundColor: "#dc3545",
          color: "white",
          textTransform: "none",
          borderRadius: "8px",
          padding: "8px 16px",
          "&:hover": { backgroundColor: "#c82333" },
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: "120px"
        }}
        onClick={downloadPDF}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <>
            <CircularProgress size={20} color="inherit" />
            Downloading...
          </>
        ) : (
          "Download PDF"
        )}
      </Button>
    </Box>
  );
};

export default DownloadButtons;
